<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Purchase',
  },
})

const route = useRoute()
const router = useRouter()
const { can } = useAbility()
const { loading: statusLoading, request } = useApiRequest()
const { loading: deleteLoading, request: deleteRequest } = useApiRequest()

// ─── State ────────────────────────────────────────────────────────────────────
const po          = ref(null)
const fetchLoading = ref(true)
const fetchError   = ref('')
const statusDialog = ref(false)
const pendingStatus = ref('')
const deleteDialog = ref(false)

// ─── Constants ────────────────────────────────────────────────────────────────
const statusColor = { draft: 'default', sent: 'info', partial: 'warning', received: 'success', cancelled: 'error' }
const statusLabel = { draft: 'Draft', sent: 'Dikirim ke Supplier', partial: 'Sebagian Diterima', received: 'Selesai', cancelled: 'Dibatalkan' }

const formatRupiah = val =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val ?? 0)

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'

// ─── Fetch ────────────────────────────────────────────────────────────────────
const fetchPo = async () => {
  fetchLoading.value = true
  fetchError.value   = ''
  try {
    const response = await useApi(`/api/v1/purchase-orders/${route.params.id}`).get()
    const body = response?.data ?? response
    po.value = body?.data ?? body
  } catch (err) {
    fetchError.value = 'Gagal memuat data purchase order.'
    console.error('[PODetail] fetch error', err)
  } finally {
    fetchLoading.value = false
  }
}

onMounted(fetchPo)

// ─── Grand total ─────────────────────────────────────────────────────────────
const grandTotal = computed(() =>
  (po.value?.items ?? []).reduce((s, i) => s + (i.subtotal ?? 0), 0)
)

// ─── Status update ────────────────────────────────────────────────────────────
const openStatusDialog = (status) => {
  pendingStatus.value = status
  statusDialog.value  = true
}

const confirmStatus = async () => {
  const result = await request(() =>
    useApi(`/api/v1/purchase-orders/${po.value.id}/status`).patch({
      body: { status: pendingStatus.value },
    })
  )

  if (result !== null) {
    statusDialog.value = false
    await fetchPo()
  }
}

// ─── Dialog label helpers ─────────────────────────────────────────────────────
const dialogTitle = computed(() =>
  pendingStatus.value === 'sent' ? 'Kirim PO ke Supplier?' : 'Batalkan Purchase Order?'
)
const dialogBody = computed(() =>
  pendingStatus.value === 'sent'
    ? 'Setelah dikirim, PO tidak dapat diubah kembali ke draft. Pastikan semua item sudah benar.'
    : 'PO yang dibatalkan tidak dapat dipulihkan. Lanjutkan?'
)
const dialogBtnLabel = computed(() =>
  pendingStatus.value === 'sent' ? 'Ya, Kirim' : 'Ya, Batalkan'
)
const dialogBtnColor = computed(() =>
  pendingStatus.value === 'sent' ? 'info' : 'error'
)

// ─── Hapus PO (hanya draft) ───────────────────────────────────────────────────
const confirmDeletePo = async () => {
  const result = await deleteRequest(() =>
    useApi(`/api/v1/purchase-orders/${po.value.id}`).delete()
  )

  if (result !== null) {
    deleteDialog.value = false
    router.push({ name: 'purchase-orders' })
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <!-- Loading -->
      <VProgressLinear v-if="fetchLoading" indeterminate class="mb-4" />

      <!-- Error -->
      <VAlert v-else-if="fetchError" type="error" class="mb-4">
        {{ fetchError }}
      </VAlert>

      <template v-else-if="po">
        <!-- ─── Header Card ──────────────────────────────────────────────── -->
        <VCard class="mb-4">
          <VCardText class="d-flex flex-wrap gap-4 align-start justify-space-between pa-5">
            <!-- Info -->
            <div>
              <div class="text-h6 font-weight-bold">{{ po.po_number }}</div>
              <div class="text-body-2 text-medium-emphasis mt-1">
                {{ po.supplier?.name ?? '-' }}
              </div>
            </div>

            <!-- Actions -->
            <div class="d-flex flex-wrap gap-2 align-center">
              <VChip :color="statusColor[po.status]" label>
                {{ statusLabel[po.status] ?? po.status }}
              </VChip>

              <VBtn
                v-if="can('update', 'Purchase') && po.is_editable"
                :to="{ name: 'purchase-orders-id-edit', params: { id: po.id } }"
                variant="tonal"
                size="small"
                prepend-icon="ri-pencil-line"
              >
                Edit
              </VBtn>

              <VBtn
                v-if="can('update', 'Purchase') && po.status === 'draft'"
                color="info"
                variant="tonal"
                size="small"
                prepend-icon="ri-send-plane-line"
                @click="openStatusDialog('sent')"
              >
                Kirim ke Supplier
              </VBtn>

              <VBtn
                v-if="can('update', 'Purchase') && po.is_cancellable"
                color="error"
                variant="tonal"
                size="small"
                prepend-icon="ri-close-circle-line"
                @click="openStatusDialog('cancelled')"
              >
                Batalkan PO
              </VBtn>

              <VBtn
                v-if="can('delete', 'Purchase') && po.is_editable"
                color="error"
                variant="tonal"
                size="small"
                prepend-icon="ri-delete-bin-line"
                @click="deleteDialog = true"
              >
                Hapus PO
              </VBtn>

              <VBtn
                v-if="can('create', 'Purchase') && ['sent', 'partial'].includes(po.status)"
                :to="{ name: 'purchase-receivings-create', query: { po_id: po.id } }"
                color="success"
                size="small"
                prepend-icon="ri-archive-line"
              >
                Buat Receiving
              </VBtn>
            </div>
          </VCardText>

          <VDivider />

          <!-- Meta info -->
          <VCardText class="pa-5">
            <VRow>
              <VCol cols="6" md="3">
                <p class="text-caption text-medium-emphasis mb-1">Tgl. Order</p>
                <p class="text-body-2">{{ formatDate(po.ordered_at) }}</p>
              </VCol>
              <VCol cols="6" md="3">
                <p class="text-caption text-medium-emphasis mb-1">Estimasi Tiba</p>
                <p class="text-body-2">{{ formatDate(po.expected_at) }}</p>
              </VCol>
              <VCol cols="6" md="3">
                <p class="text-caption text-medium-emphasis mb-1">Dibuat Oleh</p>
                <p class="text-body-2">{{ po.creator?.name ?? '-' }}</p>
              </VCol>
              <VCol cols="6" md="3">
                <p class="text-caption text-medium-emphasis mb-1">Total Pesanan</p>
                <p class="text-body-1 font-weight-bold text-primary">
                  {{ formatRupiah(po.total_amount) }}
                </p>
              </VCol>
              <VCol v-if="po.notes" cols="12">
                <p class="text-caption text-medium-emphasis mb-1">Catatan</p>
                <p class="text-body-2">{{ po.notes }}</p>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>

        <!-- ─── Items Card ───────────────────────────────────────────────── -->
        <VCard>
          <VCardTitle class="pa-5 pb-3">Item Pesanan</VCardTitle>
          <VDivider />

          <VTable>
            <thead>
              <tr>
                <th>Bahan Baku</th>
                <th>Satuan</th>
                <th class="text-end">Qty</th>
                <th class="text-end">Harga Satuan</th>
                <th class="text-end">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in po.items" :key="item.id">
                <td>{{ item.ingredient_name }}</td>
                <td>{{ item.unit_symbol }}</td>
                <td class="text-end">{{ item.quantity_ordered }}</td>
                <td class="text-end">{{ formatRupiah(item.unit_cost) }}</td>
                <td class="text-end">{{ formatRupiah(item.subtotal) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4" class="text-end font-weight-semibold py-3 pe-3">Total:</td>
                <td class="text-end font-weight-bold text-primary text-body-1">
                  {{ formatRupiah(grandTotal) }}
                </td>
              </tr>
            </tfoot>
          </VTable>
        </VCard>
      </template>
    </VCol>
  </VRow>

  <!-- ─── Status Confirmation Dialog ──────────────────────────────────────── -->
  <VDialog v-model="statusDialog" max-width="440" persistent>
    <VCard>
      <VCardTitle class="pt-5 px-5">{{ dialogTitle }}</VCardTitle>
      <VCardText class="px-5">{{ dialogBody }}</VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn variant="tonal" :disabled="statusLoading" @click="statusDialog = false">
          Batal
        </VBtn>
        <VBtn
          :color="dialogBtnColor"
          :loading="statusLoading"
          @click="confirmStatus"
        >
          {{ dialogBtnLabel }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- ─── Hapus PO Confirmation Dialog ────────────────────────────────────── -->
  <VDialog v-model="deleteDialog" max-width="440" persistent>
    <VCard>
      <VCardTitle class="pt-5 px-5">Hapus Purchase Order?</VCardTitle>
      <VCardText class="px-5">
        PO <strong>{{ po?.po_number }}</strong> beserta semua itemnya akan dihapus
        permanen dan tidak bisa dipulihkan. Hanya PO berstatus Draft yang bisa
        dihapus — kalau sudah dikirim ke supplier, gunakan "Batalkan PO" sebagai
        gantinya.
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn variant="tonal" :disabled="deleteLoading" @click="deleteDialog = false">
          Batal
        </VBtn>
        <VBtn color="error" :loading="deleteLoading" @click="confirmDeletePo">
          Ya, Hapus
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
